import React from 'react';
import { motion } from 'framer-motion';
import Section from './ui/section.jsx';
import Cards from './ui/Cards.jsx';
import { Zap, Cpu, Sparkles } from 'lucide-react';

export default function About() {
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        duration: 0.6,
        ease: "easeOut"
      }
    })
  };

  return (
    <section
      id="about"
      className="relative flex flex-col items-center text-center mx-auto px-6 py-24 gap-12 overflow-hidden"
    >
      {/* Retro gradient background */}
      <div className="absolute inset-0 w-full bg-gradient-to-b from-black via-purple-950/60 to-black -z-10" />
      <div className="absolute top-0 left-1/2 bg-gradient-radial from-fuchsia-500/20 via-transparent to-transparent blur-3xl -z-10 w-96 h-96" />
      <div className="absolute bottom-0 right-1/4 bg-gradient-radial from-cyan-500/20 via-transparent to-transparent blur-3xl -z-10 w-96 h-96" />

      {/* Heading */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="flex-1 text-center"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Section label="About Project" />
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-5xl md:text-6xl font-extrabold mb-6 tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-500 to-purple-600 drop-shadow-[0_0_20px_rgba(255,0,255,0.6)]"
        >
          About <span className="text-fuchsia-500">Upscaler</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-gray-300 max-w-3xl mx-auto leading-relaxed text-lg md:text-xl"
        >
          <span className="text-cyan-400 font-semibold">Upscaler</span> is a
          futuristic AI tool designed to redefine image clarity. Crafted for
          creators and innovators, it transforms low-res visuals into stunning
          high-quality results — all within a sleek neon interface inspired by
          80s retro tech.
        </motion.p>
      </motion.div>

      {/* Feature highlights */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="flex flex-wrap justify-center gap-6 max-w-4xl mb-8"
      >
        {[
          { icon: <Zap size={24} />, text: "Lightning Fast" },
          { icon: <Cpu size={24} />, text: "AI Powered" },
          { icon: <Sparkles size={24} />, text: "High Quality" }
        ].map((feature, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 + i * 0.1 }}
            className="flex items-center gap-2 px-4 py-2 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-full text-fuchsia-300"
          >
            {feature.icon}
            <span className="text-sm font-semibold">{feature.text}</span>
          </motion.div>
        ))}
      </motion.div>

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mt-4">
        <motion.div
          custom={0}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={cardVariants}
        >
          <Cards
            icon={<img src="/image.jpg" alt="AI Enhancement" className="rounded-xl object-cover w-full h-48" />}
            title="AI-Powered Enhancement"
            description="Deep learning algorithms intelligently upscale images while maintaining natural edges and texture fidelity."
          />
        </motion.div>
        <motion.div
          custom={1}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={cardVariants}
        >
          <Cards
            icon={<img src="/speed.jpg" alt="Fast Processing" className="rounded-xl object-cover w-full h-48" />}
            title="Fast Processing"
            description="Experience near-instant results with GPU-accelerated processing — speed that matches creativity."
          />
        </motion.div>
        <motion.div
          custom={2}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={cardVariants}
        >
          <Cards
            icon={<img src="/user-friendly.jpg" alt="User-Friendly Interface" className="rounded-xl object-cover w-full h-48" />}
            title="User-Friendly Interface"
            description="An elegant drag-and-drop interface that feels as effortless as it looks — no setup, no complexity, pure flow."
          />
        </motion.div>
      </div>
    </section>
  );
}
